// Cancels a question only through the command alias registered by its
// required event; pending reconciliation remains envelope-owned. A
// superseded cause means a new user message retired the question, drawn as
// expired rather than as a cancellation the user chose.
import { z } from 'zod';

import { defineCompoundMember } from '@apps/web/features/phoebe_chat_v3/registry/types';

const userQuestionCancelledPayloadSchema = z
    .object({
        command_id: z.uuid(),
        // Any unknown cause reads as a plain cancellation, so a future value
        // never leaves the card stuck pending.
        cause: z.string().nullable().optional().default(null),
    })
    .strip();

export const userQuestionCancelledDefinitions = {
    user_question_cancelled: defineCompoundMember('user_question_cancelled', {
        disposition: 'compound_member',
        schema: userQuestionCancelledPayloadSchema,
        malformed: { kind: 'skip' },
        evidence: null,
        slotMember: {
            slot: 'question',
            terminal: true,
            keys(payload) {
                return [
                    {
                        kind: 'command_id',
                        value: payload.command_id,
                    },
                ];
            },
            fragment(payload) {
                return {
                    kind: 'question_resolved',
                    commandId: payload.command_id,
                    resolution:
                        payload.cause === 'superseded_by_message'
                            ? 'expired'
                            : 'cancelled',
                    answer: null,
                };
            },
        },
    }),
} as const;

export const userQuestionCancelledContractFixtures = {
    valid: {
        command_id: '0198a846-739e-7a2f-a152-66a77cc11111',
        cause: 'superseded_by_message',
    },
    malformed: { command_id: 'not-a-uuid' },
} as const;
